import React, { useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import './updateSubmission.css';

const SubmissionForm = () => {
    const [guideline, setGuideline] = useState('');
    const [submissionProcess, setSubmissionProcess] = useState(['']);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const navigate = useNavigate();

    const addProcessStep = () => {
        setSubmissionProcess([...submissionProcess, '']);
    };

    const removeProcessStep = (index) => {
        if (submissionProcess.length > 1) {
            const updatedProcess = submissionProcess.filter((_, i) => i !== index);
            setSubmissionProcess(updatedProcess);
        }
    };

    const handleProcessChange = (index, value) => {
        const updatedProcess = [...submissionProcess];
        updatedProcess[index] = value;
        setSubmissionProcess(updatedProcess);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation
        if (!guideline.trim()) {
            Swal.fire({
                icon: 'warning',
                title: 'Missing Information',
                text: 'Please provide submission guidelines.',
                confirmButtonColor: '#5e71f6',
            });
            return;
        }

        const validProcessSteps = submissionProcess.filter(step => step.trim() !== '');
        if (validProcessSteps.length === 0) {
            Swal.fire({
                icon: 'warning',
                title: 'Missing Information',
                text: 'Please add at least one submission process step.',
                confirmButtonColor: '#5e71f6',
            });
            return;
        }

        setIsSubmitting(true);

        try {
            const response = await axios.put(
                `${process.env.REACT_APP_BACKEND_SERVER_URL}/api/v1/admin/update-submission`,
                {
                    guideline,
                    submissionProcess: validProcessSteps,
                }
            );

            if (response.status === 200) {
                Swal.fire({
                    icon: 'success',
                    title: 'Updated!',
                    text: 'Submission guidelines updated successfully!',
                    confirmButtonColor: '#5e71f6',
                });
                navigate('/submission-guideline');
            }
        } catch (error) {
            console.error('Error updating submission guidelines:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error!',
                text: 'An error occurred while updating. Please try again.',
                confirmButtonColor: '#5e71f6',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="update-submission-wrapper">
            <div className="update-submission-container">
                {/* Header Section */}
                <div className="submission-header">
                    <h1 className="submission-title">Update Submission Guidelines</h1>
                    <p className="submission-subtitle">Configure paper submission guidelines and process steps</p>
                </div>

                {/* Form Card */}
                <div className="submission-form-card">
                    <div className="submission-form-header">
                        <h2 className="form-card-title">
                            <svg className="form-header-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            Submission Information
                        </h2>
                    </div>

                    <form onSubmit={handleSubmit} className="submission-form">
                        {/* Submission Guideline */}
                        <div className="form-field">
                            <label className="form-label">
                                <svg className="label-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                Submission Guidelines <span className="required-mark">*</span>
                            </label>
                            <textarea
                                required
                                className="form-textarea"
                                rows="8"
                                value={guideline}
                                onChange={(e) => setGuideline(e.target.value)}
                                placeholder="Enter detailed submission guidelines for authors..."
                            />
                            <div className="character-count">
                                {guideline.length} characters
                            </div>
                        </div>

                        {/* Submission Process Steps */}
                        <div className="form-field">
                            <label className="form-label">
                                <svg className="label-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                                </svg>
                                Submission Process Steps <span className="required-mark">*</span>
                            </label>

                            <div className="process-steps-container">
                                {submissionProcess.map((step, index) => (
                                    <div key={index} className="process-step-item">
                                        <div className="step-number">{index + 1}</div>
                                        <input
                                            type="text"
                                            className="form-input process-step-input"
                                            value={step}
                                            onChange={(e) => handleProcessChange(index, e.target.value)}
                                            placeholder={`Enter step ${index + 1} of the submission process...`}
                                        />
                                        {submissionProcess.length > 1 && (
                                            <button
                                                type="button"
                                                className="remove-step-button"
                                                onClick={() => removeProcessStep(index)}
                                                title="Remove this step"
                                            >
                                                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                </svg>
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>

                            <button
                                type="button"
                                className="add-step-button"
                                onClick={addProcessStep}
                            >
                                <svg className="button-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                </svg>
                                Add Another Step
                            </button>
                        </div>

                        {/* Info Box */}
                        <div className="info-box">
                            <svg className="info-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div className="info-content">
                                <strong>Note:</strong> Guidelines and process steps will be displayed to authors on the submission page. Make sure all information is clear and accurate.
                            </div>
                        </div>

                        {/* Submit Button */}
                        <button
                            type="submit"
                            className="submit-button"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? (
                                <>
                                    <svg className="button-icon spinner" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                    </svg>
                                    Updating...
                                </>
                            ) : (
                                <>
                                    <svg className="button-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                    </svg>
                                    Update Guidelines
                                </>
                            )}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default SubmissionForm;
